import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

// Extend cache TTL from 5 minutes to 30 minutes for better performance
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const cacheKey = (userId) => `cache_ownership_${userId || 'anon'}`;

function readCache(userId) {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    if (!obj.ts || Date.now() - obj.ts > CACHE_TTL_MS) return null;
    return obj.data;
  } catch {
    return null;
  }
}

function writeCache(userId, data) {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* ignore cache write errors */
  }
}

const OwnershipContext = createContext({
  ownedPackages: new Set(),
  ownedTests: new Set(),
  premiumExamIds: new Set(),
  ownedCategoryIds: new Set(),
  loaded: false,
  refreshOwnership: async () => {},
});

export const OwnershipProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [ownedPackages, setOwnedPackages] = useState(new Set());
  const [ownedTests, setOwnedTests] = useState(new Set());
  const [premiumExamIds, setPremiumExamIds] = useState(new Set());
  const [ownedCategoryIds, setOwnedCategoryIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const isRefreshingRef = useRef(false);

  const hydrateFromCache = useCallback(() => {
    const cached = readCache(userId);
    if (cached && typeof cached === 'object') {
      setOwnedPackages(new Set(cached.ownedPackages || []));
      setOwnedTests(new Set(cached.ownedTests || []));
      setPremiumExamIds(new Set(cached.premiumExamIds || []));
      setOwnedCategoryIds(new Set(cached.ownedCategoryIds || []));
      setLoaded(true);
    }
  }, [userId]);

  const refreshOwnership = useCallback(
    async (force = false) => {
      if (!userId) {
        setOwnedPackages(new Set());
        setOwnedTests(new Set());
        setPremiumExamIds(new Set());
        setOwnedCategoryIds(new Set());
        setLoaded(true);
        return;
      }

      // Don't refresh if already refreshing unless forced
      if (isRefreshingRef.current && !force) return;
      isRefreshingRef.current = true;

      try {
        // Check cache first unless forced refresh
        if (!force) {
          const cached = readCache(userId);
          if (cached && typeof cached === 'object') {
            setOwnedPackages(new Set(cached.ownedPackages || []));
            setOwnedTests(new Set(cached.ownedTests || []));
            setPremiumExamIds(new Set(cached.premiumExamIds || []));
            setOwnedCategoryIds(new Set(cached.ownedCategoryIds || []));
            setLoaded(true);
            isRefreshingRef.current = false;
            return;
          }
        }

        const { data, error } = await supabase
          .from('user_purchases')
          .select('package_id, status, valid_until')
          .eq('user_id', userId);
        if (error) {
          console.debug('ownership fetch error', error);
          return;
        }

        const pkgSet = new Set();
        (data || []).forEach((row) => {
          const valid = row.valid_until ? new Date(row.valid_until).getTime() >= Date.now() : true;
          if (row.status === 'paid' && valid) {
            if (row.package_id) pkgSet.add(row.package_id);
          }
        });
        setOwnedPackages(pkgSet);
        setOwnedTests(new Set());

        // Map purchases to exam IDs and Category IDs - batch the queries
        const examIds = new Set();
        const categoryIds = new Set();
        const promises = [];

        if (pkgSet.size > 0) {
          promises.push(
            supabase
              .from('premium_packages')
              .select('id, exam_id, category_id')
              .in('id', Array.from(pkgSet))
          );
        }

        // Execute all queries in parallel
        const results = await Promise.all(promises);

        results.forEach((result, index) => {
          if (result.data) {
            result.data.forEach((item) => {
              if (item.exam_id) examIds.add(item.exam_id);
              if (item.category_id) categoryIds.add(item.category_id);
            });
          }
        });

        // If we have category IDs, fetch all exams for these categories
        if (categoryIds.size > 0) {
          const { data: catExams } = await supabase
            .from('exams')
            .select('id')
            .in('category_id', Array.from(categoryIds));
          (catExams || []).forEach((e) => examIds.add(e.id));
        }

        setPremiumExamIds(examIds);
        setOwnedCategoryIds(categoryIds);

        writeCache(userId, {
          ownedPackages: Array.from(pkgSet),
          ownedTests: [],
          premiumExamIds: Array.from(examIds),
          ownedCategoryIds: Array.from(categoryIds),
        });
        setLoaded(true);
      } catch (e) {
        console.debug('ownership fetch exception', e);
      } finally {
        isRefreshingRef.current = false;
      }
    },
    [userId]
  );

  useEffect(() => {
    // on user change: hydrate cache and refresh; don't clear to avoid flicker
    hydrateFromCache();
    let cancelled = false;

    // Background refresh - don't block UI if we already loaded from cache
    (async () => {
      await refreshOwnership();
      if (!cancelled) {
        setLoaded(true);
      }
    })();

    // Realtime purchase updates for immediate entitlement changes
    let channel;
    try {
      if (userId) {
        channel = supabase
          .channel(`purchases-${userId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'user_purchases',
              filter: `user_id=eq.${userId}`,
            },
            () => {
              refreshOwnership(true); // Force refresh on real-time updates
            }
          )
          .subscribe();
      }
    } catch (e) {
      /* subscribe not available; ignore */
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        const cached = readCache(userId);
        if (!cached) refreshOwnership();
      }
    };
    const onOwnershipChanged = () => {
      refreshOwnership(true); // Force refresh
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('ownership-changed', onOwnershipChanged);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('ownership-changed', onOwnershipChanged);
      if (channel) supabase.removeChannel(channel);
    };
  }, [userId]);

  const value = useMemo(
    () => ({
      ownedPackages,
      ownedTests,
      premiumExamIds,
      ownedCategoryIds,
      loaded,
      refreshOwnership,
    }),
    [ownedPackages, ownedTests, premiumExamIds, ownedCategoryIds, loaded, refreshOwnership]
  );

  return <OwnershipContext.Provider value={value}>{children}</OwnershipContext.Provider>;
};

export const useOwnership = () => useContext(OwnershipContext);
